import {Component, EventEmitter, HostListener, NgZone, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {Session} from "../../services/Session";
import {LoadingService} from "../../services/LoadingService";
import {ToastService} from "../../services/Toast.service";
import {SwitchBoard} from "../../services/SwitchBoard";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {FormulaTestCaseService} from "../../tpiq-services/formula-test-case.service";
import {FormulaTestCaseFormComponent} from "../formula-test-case-form/formula-test-case-form.component";
import {formulaTypeLabel} from "../formula-type-labels";

@Component({
  selector: 'app-formula-test-case-list',
  standalone: true,
  imports: [CommonModule, FormsModule, FormulaTestCaseFormComponent],
  templateUrl: './formula-test-case-list.component.html',
  styleUrl: './formula-test-case-list.component.scss'
})
export class FormulaTestCaseListComponent implements OnInit, OnDestroy {
  org: string;
  user: string;
  testCases: any[] = [];
  loading = false;

  reveal = false;
  editMode = false;
  selectedTestCase: any = null;
  searchKey = '';
  typeFilter = '';
  lastRunFilter = '';
  statusFilter = 'ACTIVE';

  page = 1;
  pageSize = 25;
  totalCount = 0;
  readonly pageSizeOptions = [10, 25, 50, 100];
  private search$ = new Subject<string>();

  runAllResult: any = null;
  openMenuId: string | null = null;
  // Hidden by default; enable from the browser console with
  //   enableFormulaDryRun()        (or pass false to hide again)
  // or persistently via localStorage.setItem('formulaDryRunEnabled', 'true') + reload.
  dryRunEnabled = false;

  historyReveal = false;
  historyTestCase: any = null;
  historyRuns: any[] = [];
  historyLoading = false;

  destroy$: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private formulaTestCaseService: FormulaTestCaseService,
    private ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.org = Session.getOrg();
    this.user = Session.getUser().id;
    this.dryRunEnabled = localStorage.getItem('formulaDryRunEnabled') === 'true';
    (window as any).enableFormulaDryRun = (enabled = true) => {
      // Console calls run outside Angular's zone — re-enter it so the button appears immediately.
      this.ngZone.run(() => {
        this.dryRunEnabled = enabled;
        localStorage.setItem('formulaDryRunEnabled', String(enabled));
      });
      return `Dry run ${enabled ? 'enabled' : 'disabled'}`;
    };
    this.getTestCases();
    this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.getTestCases();
    });
    this.switchBoard.onToastConfirm.pipe(takeUntil(this.destroy$)).subscribe(toast => {
      if (toast.data) {
        this.removeConfirmed(toast.data);
      }
    });
  }

  ngOnDestroy(): void {
    delete (window as any).enableFormulaDryRun;
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  getTestCases() {
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.getTestCases(this.org, this.user, {
      page: this.page,
      pageSize: this.pageSize,
      search: (this.searchKey || '').trim(),
      formulaType: this.typeFilter,
      lastRunStatus: this.lastRunFilter,
      status: this.statusFilter
    }).subscribe((response) => {
      this.loadingService.triggerLoadingEvent(false);
      this.testCases = response?.items || [];
      this.totalCount = response?.totalCount || 0;
      // The server clamps out-of-range pages (e.g. after deleting the last row
      // of the last page) — adopt whatever page it actually served.
      this.page = response?.page || 1;
    }, (error) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to load formula test cases');
    });
  }

  onSearchChange() {
    this.search$.next(this.searchKey);
  }

  onFilterChange() {
    this.page = 1;
    this.getTestCases();
  }

  onPageSizeChange() {
    this.page = 1;
    this.getTestCases();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  prevPage() {
    if (this.page > 1) {
      this.page--;
      this.getTestCases();
    }
  }

  nextPage() {
    if (this.page < this.totalPages()) {
      this.page++;
      this.getTestCases();
    }
  }

  hasAnyFilter(): boolean {
    return !!(this.searchKey || '').trim() || !!this.typeFilter || !!this.lastRunFilter || this.statusFilter !== 'ACTIVE';
  }

  addNew() {
    this.selectedTestCase = null;
    this.editMode = false;
    this.reveal = true;
  }

  edit(testCase) {
    this.selectedTestCase = testCase;
    this.editMode = true;
    this.reveal = true;
  }

  duplicate(testCase) {
    this.selectedTestCase = {
      ...testCase,
      id: null,
      name: `${testCase.name} (Copy)`,
      active: true,
      lastRunStatus: null,
      lastRunActualResult: null,
      lastRunAt: null,
      createdBy: null,
      createdDate: null,
      updatedDate: null
    };
    this.editMode = false;
    this.reveal = true;
  }

  hideReveal() {
    this.reveal = false;
    this.selectedTestCase = null;
  }

  onSaved() {
    this.reveal = false;
    this.selectedTestCase = null;
    this.getTestCases();
  }

  remove(testCase) {
    this.toastService.pop(TOAST_TYPE.confirm, 'Are you sure you want to delete this test case?', testCase);
  }

  private removeConfirmed(testCase) {
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.deleteTestCase(this.org, this.user, testCase.id).subscribe(() => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.success, 'Test case deleted successfully');
      this.getTestCases();
    }, (error) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to delete test case');
    });
  }

  run(testCase) {
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.runTestCase(this.org, this.user, testCase.id).subscribe((result) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(result.passed ? TOAST_TYPE.success : TOAST_TYPE.error,
        result.passed ? `${result.name}: PASS` : `${result.name}: FAIL (expected ${result.expectedResult}, got ${result.actualResult})`);
      this.getTestCases();
    }, (error) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to run test case');
    });
  }

  runAll(dryRun = false) {
    this.runAllResult = null;
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.runAllTestCases(this.org, this.user, dryRun).subscribe((results) => {
      this.loadingService.triggerLoadingEvent(false);
      const all = results || [];
      const failed = all.filter(r => !r.passed);
      // Shown in a persistent panel (not a toast) so results — especially
      // failures — stay visible until dismissed or the next run.
      this.runAllResult = {total: all.length, failed, at: new Date(), dryRun};
      if (!dryRun) {
        this.getTestCases();
      }
    }, (error) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to run test cases');
    });
  }

  toggleActive(testCase) {
    const nextActive = !testCase.active;
    this.loadingService.triggerLoadingEvent(true);
    this.formulaTestCaseService.setActive(this.org, this.user, testCase.id, nextActive).subscribe(() => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.success, nextActive ? 'Test case activated' : 'Test case deactivated');
      this.getTestCases();
    }, (error) => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to update test case status');
    });
  }

  viewHistory(testCase) {
    this.historyTestCase = testCase;
    this.historyReveal = true;
    this.historyRuns = [];
    this.historyLoading = true;
    this.formulaTestCaseService.getRunHistory(this.org, this.user, testCase.id).subscribe((runs) => {
      this.historyLoading = false;
      this.historyRuns = runs || [];
    }, (error) => {
      this.historyLoading = false;
      this.toastService.pop(TOAST_TYPE.error, error.message || 'Failed to load run history');
    });
  }

  hideHistory() {
    this.historyReveal = false;
    this.historyTestCase = null;
    this.historyRuns = [];
  }

  formulaTypeLabel(type: string): string {
    return formulaTypeLabel(type);
  }

  toggleMenu(testCase, event: Event) {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === testCase.id ? null : testCase.id;
  }

  closeMenu() {
    this.openMenuId = null;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.closeMenu();
  }

}
